import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { CREDIT_COSTS, isUnlimited, getPlan } from '../lib/creditConfig';

const CreditsContext = createContext(null);

export function CreditsProvider({ children }) {
  const { user } = useAuth();
  const [credits, setCredits] = useState(null);
  const [plan, setPlan] = useState('free');
  const [unlimitedOverride, setUnlimitedOverride] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchBalance = useCallback(async () => {
    if (!user?.id) return;
    try {
      const res = await fetch(`/api/credits/balance?userId=${user.id}`);
      if (!res.ok) throw new Error('Failed to fetch credits');
      const data = await res.json();
      setCredits(data.credits);
      setPlan(data.plan || 'free');
      setUnlimitedOverride(!!data.unlimited);
      setError(null);
    } catch (err) {
      console.error('[Credits] fetch error:', err);
      setError(err.message);
      // Show free plan defaults so the UI isn't blank while migration is pending
      setCredits(prev => prev === null ? 500 : prev);
      setPlan(prev => prev === 'free' ? 'free' : prev);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    if (user?.id) {
      setLoading(true);
      fetchBalance();
    } else {
      setLoading(false);
    }
  }, [user?.id, fetchBalance]);

  const canAfford = useCallback((actionKey) => {
    if (isUnlimited(plan)) return true;
    if (credits === null) return false;
    const cost = CREDIT_COSTS[actionKey]?.cost ?? 0;
    return credits >= cost;
  }, [credits, plan]);

  const spendCredits = useCallback(async (actionKey, { description, prospectId } = {}) => {
    if (!user?.id) return { success: false, error: 'Not authenticated' };
    if (isUnlimited(plan)) return { success: true };

    const actionDef = CREDIT_COSTS[actionKey];
    if (!actionDef) return { success: false, error: 'Unknown action' };
    if (credits < actionDef.cost) {
      return { success: false, error: `Not enough credits. You need ${actionDef.cost} credits.` };
    }

    try {
      const res = await fetch('/api/credits/use', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: user.id,
          action: actionDef.action,
          credits: actionDef.cost,
          description: description || actionDef.description,
          prospectId: prospectId || null,
        }),
      });
      const data = await res.json();
      if (!res.ok) return { success: false, error: data.error || 'Failed to spend credits' };
      setCredits(data.creditsRemaining);
      return { success: true, creditsRemaining: data.creditsRemaining };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, [user?.id, plan, credits]);

  const grantCredits = useCallback(async (amount, description) => {
    if (!user?.id) return;
    await fetch('/api/credits/grant', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId: user.id, amount, description }),
    });
    await fetchBalance();
  }, [user?.id, fetchBalance]);

  const planInfo = getPlan(plan);
  const unlimited = unlimitedOverride || isUnlimited(plan);

  return (
    <CreditsContext.Provider value={{
      credits,
      plan,
      planInfo,
      unlimited,
      loading,
      error,
      canAfford,
      spendCredits,
      grantCredits,
      refreshCredits: fetchBalance,
    }}>
      {children}
    </CreditsContext.Provider>
  );
}

export function useCredits() {
  const ctx = useContext(CreditsContext);
  if (!ctx) throw new Error('useCredits must be used within CreditsProvider');
  return ctx;
}
